import { cva, type VariantProps } from 'class-variance-authority';
import { CircleCheck, Info, Lightbulb, OctagonAlert, TriangleAlert } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Exported so a block the app draws itself — a whole panel that has gone into
 * a failed state, say — can wear the same skin without re-deriving the tints.
 */
export const calloutVariants = cva(
  cn(
    'flex items-start gap-3 rounded-base border p-4 text-left',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
  ),
  {
    variants: {
      /**
       * Badge's vocabulary minus `neutral`, deliberately. Status colour is
       * reserved for real status, and a block with nothing at stake is prose —
       * it does not need a border, a tint and a glyph to be read.
       *
       * `brand` (slice 65) is not a status either, and it is here for the one
       * kind of block that is not prose and not a verdict: a note about how
       * Wend works that a person should see before they act — "nothing is
       * published until you press start". Violet, because violet means Wend
       * and cannot be mistaken for good news or a warning.
       */
      tone: {
        info: 'border-info/35 bg-info/[0.07]',
        good: 'border-good/35 bg-good/[0.07]',
        warn: 'border-warn/35 bg-warn/[0.07]',
        bad: 'border-bad/40 bg-bad/[0.07]',
        brand: 'border-brand-b/35 bg-brand-a/[0.07]',
      },
    },
    defaultVariants: {
      tone: 'info',
    },
  },
);

export type CalloutTone = NonNullable<VariantProps<typeof calloutVariants>['tone']>;

/**
 * How much room the block takes (slice 66). `compact` is the Blueprint's
 * in-panel warning (`.sb-warn`): tighter padding, smaller type, for a callout
 * that sits inside a figure panel beside the number it qualifies rather than
 * across the top of a screen. One component for both, so there is no second
 * amber card to drift — the concept had five.
 *
 * Each size carries its own line-height, written AFTER the size in the same
 * string. `cn` is tailwind-merge, and tailwind-merge drops a `leading-*` class
 * whenever a font-size class follows it in the same call — so a base string of
 * `leading-snug` followed by `size.title` rendered with no line-height at all,
 * on every Callout in both apps (slice 66 review). Size-then-leading inside one
 * entry is the order the merge keeps; verify-ui-suite renders both densities
 * and counts the leading classes that reach the markup.
 */
const CALLOUT_DENSITY = {
  default: { root: '', title: 'text-[0.92rem] leading-snug', body: 'text-[0.875rem] leading-relaxed' },
  compact: {
    root: 'gap-2.5 rounded-base px-3 py-2.5',
    title: 'text-[0.8rem] leading-snug',
    body: 'text-[0.76rem] leading-relaxed',
  },
} as const;

export type CalloutDensity = keyof typeof CALLOUT_DENSITY;

/**
 * Per tone: the glyph colour, the glyph itself, and the word a screen reader
 * hears in place of both.
 *
 * The glyphs are five different *silhouettes* — circle, tick-in-circle,
 * triangle, octagon, and since slice 65 a bulb for `brand` — not one glyph in
 * five colours. That is what makes the tone survive a reader who cannot
 * separate #F5B851 from #3DD68C.
 */
const toneDetail: Record<CalloutTone, { icon: string; Icon: typeof Info; word: string }> = {
  info: { icon: 'text-info', Icon: Info, word: 'Information' },
  good: { icon: 'text-good', Icon: CircleCheck, word: 'Success' },
  warn: { icon: 'text-warn', Icon: TriangleAlert, word: 'Warning' },
  bad: { icon: 'text-bad', Icon: OctagonAlert, word: 'Error' },
  brand: { icon: 'text-brand-b', Icon: Lightbulb, word: 'Note' },
};

/**
 * Whether the Callout is announced when it appears, and how insistently.
 * See the component comment — the right answer belongs to the caller.
 */
export type CalloutLive = 'off' | 'polite' | 'assertive';

export interface CalloutProps
  // `title` and `role` are ours: the DOM `title` attribute is a tooltip string
  // and would collide with the heading, and `role` is derived from `live` so a
  // caller cannot set a live role that contradicts what it asked for.
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'role'>,
    VariantProps<typeof calloutVariants> {
  /** First line, in full sentence case. Omit it when the body says it all. */
  title?: ReactNode;
  /**
   * Replaces the tone's glyph. It cannot remove it — `undefined` and `null`
   * both fall back to the default — because the glyph is a carrier of the
   * tone, not decoration.
   */
  icon?: ReactNode;
  /** Rendered under the body: a link to the fix, a retry button. */
  action?: ReactNode;
  /** Default `'off'`. */
  live?: CalloutLive;
  /** Default `'default'`. `compact` for a callout inside a panel. */
  density?: CalloutDensity;
}

/**
 * A toned block for something a person must not miss — the SES sandbox
 * warning, "no verified identity", the reason a credential check failed.
 *
 *     <Callout tone="warn" title="This SES account is in the sandbox"
 *       action={<a href={AWS_PRODUCTION_ACCESS_URL}>Request production access</a>}>
 *       Sending is capped at ~200 messages a day, and only to verified
 *       addresses.
 *     </Callout>
 *
 * **Colour is never the only carrier.** Three redundant ones, because colour is
 * the one a given reader may not have:
 *
 * 1. the glyph is a different shape per tone, not one shape in four colours;
 * 2. the title and body say the thing in words;
 * 3. a visually hidden tone word ("Warning", "Error", "Note") is the first
 *    text in the block, so a screen reader — which gets neither the colour nor
 *    the `aria-hidden` glyph — still hears which of the five this is.
 *
 * Under `forced-colors` the tint and the border colour are replaced by the
 * system palette and the tone vanishes; (1) survives, because the glyph is
 * `currentColor` strokes rather than a background image, and so do (2) and (3).
 * No `forced-colors:` override is needed here — unlike `Checkbox`, nothing in
 * this component is drawn with a background image.
 *
 * **`live` decides whether it is announced, and the caller decides `live`:**
 *
 * - `'off'` (the default) — no role at all. A Callout that is on the screen
 *   when it loads is met in reading order like any other prose. A live role
 *   there fires into page-load chatter and is either lost or read twice.
 * - `'polite'` → `role="status"`. It appeared because of something the reader
 *   did, and interrupting them would be rude.
 * - `'assertive'` → `role="alert"`. A failure they just caused, which they must
 *   not walk away believing succeeded.
 *
 * One caveat worth knowing: a live region *inserted* with its text already in
 * it is announced unreliably, `role="status"` much more so than `role="alert"`.
 * So for a message that must be heard, either keep the Callout mounted and let
 * its content change, or reach for `'assertive'`.
 */
export const Callout = forwardRef<HTMLDivElement, CalloutProps>(function Callout(
  { tone, title, icon, action, live = 'off', density = 'default', className, children, ...rest },
  ref,
) {
  const resolvedTone: CalloutTone = tone ?? 'info';
  const detail = toneDetail[resolvedTone];
  const { Icon } = detail;
  const size = CALLOUT_DENSITY[density];

  const hasTitle = title !== undefined && title !== null;
  const hasAction = action !== undefined && action !== null;

  return (
    <div
      // `rest` first, unlike the rest of the package: everything below is
      // derived from `live`, and a stray `aria-live` from a caller must not be
      // able to half-override a live region and leave it announcing nothing.
      {...rest}
      ref={ref}
      data-tone={resolvedTone}
      role={live === 'off' ? undefined : live === 'assertive' ? 'alert' : 'status'}
      // Implied by the role, and stated anyway: some screen readers honour one
      // of the pair and not the other. Dropzone's status region does the same.
      aria-live={live === 'off' ? undefined : live}
      className={cn(calloutVariants({ tone }), size.root, className)}
    >
      <span aria-hidden="true" className={cn('mt-0.5 flex shrink-0', detail.icon)}>
        {icon ?? <Icon />}
      </span>

      <div className="min-w-0 flex-1">
        {/* First text in the block, so it prefixes the announcement rather than
            trailing it: "Warning, This SES account is in the sandbox". */}
        <span className="sr-only">{detail.word}: </span>

        {hasTitle && (
          // A <p>, not a heading: a Callout appears inside a Card, in a step, or
          // under a field, and only the screen it lives on knows what level a
          // heading there would have to be. Nothing here belongs in the outline.
          //
          // A warning's title is `warn-ink`, the cream the concept found for
          // `.sb-warn-title` (13.49:1 on the 7% wash): `text` on amber reads
          // as a grey block, and amber on amber glares. Every other tone keeps
          // `text`. The `<p class="…font-medium…">` then `<div>` order is
          // load-bearing — verify-dashboard's `calloutSlots` reads the title
          // and the body out of the markup by it.
          <p
            className={cn(
              'font-medium',
              size.title,
              resolvedTone === 'warn' ? 'text-warn-ink' : 'text-text',
            )}
          >
            {title}
          </p>
        )}

        <div
          className={cn(
            'break-words text-muted',
            size.body,
            hasTitle && (density === 'compact' ? 'mt-0.5' : 'mt-1'),
          )}
        >
          {children}
        </div>

        {hasAction && <div className="mt-3 flex flex-wrap items-center gap-2">{action}</div>}
      </div>
    </div>
  );
});
